import CheckIcon from '@mui/icons-material/Check';
import { Box, colors, Typography, styled } from '@mui/material';
import {
  ContentContainer,
  Kicker,
  SectionH2,
  SectionHead,
} from '~/components/HomepageV3/styles';

interface UseCase {
  tag: string;
  title: string;
  body: string;
  bullets: string[];
  href: string;
}

const USE_CASES: UseCase[] = [
  {
    tag: 'Sales',
    title: 'Prospect by tech stack',
    body: 'Build target lists of companies running the exact technologies your product replaces — or complements.',
    bullets: [
      'Export to CSV or push to HubSpot',
      'Filter by employee count, country, rank',
      'Webhook on new matches',
    ],
    href: '/technologies',
  },
  {
    tag: 'Competitive',
    title: 'Watch competitor adoption',
    body: "Get notified when a domain swaps your tech for a rival's, or vice versa. Quantify churn risk before it lands.",
    bullets: [
      'Diff reports across crawls',
      'At-risk customer alerts',
      'Win-back lookalikes',
    ],
    href: '/reports/new',
  },
  {
    tag: 'Research',
    title: 'Size any market',
    body: "Count adoption across categories, geographies, and verticals. Back your slide with a number that's defensible.",
    bullets: [
      'Adoption time-series',
      'Category market share',
      'Industry & geo breakdowns',
    ],
    href: '/reports/new',
  },
];

export const UseCasesSection = () => (
  <UseCasesSectionRoot>
    <ContentContainer sx={{ py: 12 }}>
      <SectionHead>
        <Kicker>Why teams use it</Kicker>
        <SectionH2>One dataset, three jobs.</SectionH2>
      </SectionHead>

      <UseCasesGrid>
        {USE_CASES.map((useCase) => {
          return (
            <UseCaseCard key={useCase.tag}>
              <UseCaseTag>{useCase.tag}</UseCaseTag>
              <UseCaseTitle>{useCase.title}</UseCaseTitle>
              <UseCaseBody>{useCase.body}</UseCaseBody>
              <UseCaseList>
                {useCase.bullets.map((bullet) => {
                  return (
                    <UseCaseListItem key={bullet}>
                      <CheckIcon
                        sx={{
                          fontSize: 14,
                          color: colors.blue[600],
                          mt: '3px',
                          flexShrink: 0,
                        }}
                      />
                      {bullet}
                    </UseCaseListItem>
                  );
                })}
              </UseCaseList>
            </UseCaseCard>
          );
        })}
      </UseCasesGrid>
    </ContentContainer>
  </UseCasesSectionRoot>
);

const UseCasesSectionRoot = styled(Box)`
  background: ${colors.grey[50]};
  border-top: 1px solid ${colors.grey[200]};
  border-bottom: 1px solid ${colors.grey[200]};
`;

const UseCasesGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const UseCaseCard = styled(Box)`
  background: #fff;
  border: 1px solid ${colors.grey[200]};
  border-radius: 20px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition:
    box-shadow 0.2s,
    transform 0.2s;

  &:hover {
    box-shadow: 0 12px 24px -8px rgba(15, 23, 42, 0.1);
    transform: translateY(-2px);
  }
`;

const UseCaseTag = styled(Typography)`
  align-self: flex-start;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${colors.blue[50]};
  color: ${colors.blue[800]};
  padding: 4px 10px;
  border-radius: 999px;
` as typeof Typography;

const UseCaseTitle = styled(Typography)`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${colors.grey[900]};
` as typeof Typography;

const UseCaseBody = styled(Typography)`
  font-size: 15px;
  line-height: 1.55;
  color: ${colors.grey[600]};
` as typeof Typography;

const UseCaseList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UseCaseListItem = styled('li')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: ${colors.grey[800]};
`;
